import clarity from '@microsoft/clarity';
import { init as initFullStory, FullStory } from '@fullstory/browser';
import type { AuthUser } from '@/types/auth';

const isDev = import.meta.env.DEV;
const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;
const FULLSTORY_ORG_ID = import.meta.env.VITE_FULLSTORY_ORG_ID;

function log(tool: string, action: string, data?: unknown) {
  if (isDev) {
    console.log(`[Analytics:${tool}] ${action}`, data ?? '');
  }
}

let clarityInitialized = false;
let fullstoryInitialized = false;

export function initializeAnalytics() {
  // Initialize Clarity
  if (CLARITY_ID && !clarityInitialized) {
    try {
      clarity.init(CLARITY_ID);
      clarityInitialized = true;
      log('Clarity', 'Initialized', { projectId: CLARITY_ID });
    } catch (error) {
      console.error('[Analytics] Failed to initialize Clarity:', error);
    }
  } else if (!CLARITY_ID) {
    log('Clarity', 'Skipped - No project ID');
  }

  // Initialize FullStory
  if (FULLSTORY_ORG_ID && !fullstoryInitialized) {
    try {
      initFullStory({
        orgId: FULLSTORY_ORG_ID,
        devMode: isDev,
      });
      fullstoryInitialized = true;
      log('FullStory', 'Initialized', { orgId: FULLSTORY_ORG_ID, devMode: isDev });
    } catch (error) {
      console.error('[Analytics] Failed to initialize FullStory:', error);
    }
  } else if (!FULLSTORY_ORG_ID) {
    log('FullStory', 'Skipped - No org ID');
  }
}

export function identifyUser(user: AuthUser) {
  // TypeScript guarantees roles array exists, but check length for business logic
  // (new users may have empty roles array before role assignment)
  if (user.roles.length === 0) {
    log('Analytics', 'Skipped identification - user has no roles');
    return;
  }

  try {
    const primaryRole = user.roles[0].role;
    const auctionHouseSlug = user.roles[0].auctionHouse?.slug ?? null;

    const userData = {
      displayName: `${user.firstName} ${user.lastName}`,
      email: user.email,
      role: primaryRole,
      auctionHouseSlug,
    };

    // Clarity identification
    if (clarityInitialized) {
      clarity.identify(user.id, undefined, undefined, user.email);
      clarity.setTag('role', primaryRole);
      if (auctionHouseSlug) {
        clarity.setTag('auctionHouse', auctionHouseSlug);
      }
      log('Clarity', 'User identified', userData);
    }

    // FullStory identification
    if (fullstoryInitialized) {
      FullStory('setIdentity', {
        uid: user.id,
        properties: userData,
      });
      log('FullStory', 'User identified', userData);
    }
  } catch (error) {
    console.error('[Analytics] Failed to identify user:', error);
  }
}

export function clearAnalyticsIdentity() {
  // FullStory - clear identity
  if (fullstoryInitialized) {
    FullStory('setIdentity', { anonymous: true });
    log('FullStory', 'Identity cleared');
  }

  // Clarity doesn't have a clear method - resets on page reload
  if (clarityInitialized) {
    log('Clarity', 'Identity will clear on reload');
  }
}
